/**
 * Compact YouTube-video list for the chart's click-a-day PUBLIC drill — the
 * per-video provenance behind the public volume line, the counterpart to
 * ArticleList (media). Unlike GDELT, these rows already exist in
 * salienceObservations (they ARE what the volume counts), so no on-demand
 * capture. Each row links to the video; a top-channels summary rides above to
 * answer "who is making wealth-tax content?" — Gary's own uploads badged.
 */
import { ExternalLink } from "lucide-react";
import type { SalienceObservation } from "../../lib/api";
import { shortDate } from "./format";

interface ChannelTally {
  channelTitle: string;
  count: number;
  isGary: boolean;
}

function topChannels(videos: SalienceObservation[]): ChannelTally[] {
  const by = new Map<string, ChannelTally>();
  for (const v of videos) {
    const key = v.channelId || v.channelTitle;
    if (!key) continue;
    const cur = by.get(key);
    if (cur) cur.count += 1;
    else
      by.set(key, {
        channelTitle: v.channelTitle || "Unknown channel",
        count: 1,
        isGary: v.isGary,
      });
  }
  return [...by.values()].sort((a, b) => b.count - a.count).slice(0, 6);
}

export function YouTubeVideoList({ videos }: { videos: SalienceObservation[] }) {
  const channels = topChannels(videos);

  return (
    <div className="space-y-2">
      {channels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {channels.map((c) => (
            <span
              key={c.channelTitle}
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ${
                c.isGary
                  ? "bg-brand-magenta/15 text-brand-magenta"
                  : "bg-white/5 text-terminal-ink-muted"
              }`}
            >
              <span className="max-w-[10rem] truncate">{c.channelTitle}</span>
              <span className="tabular-nums opacity-70">×{c.count}</span>
            </span>
          ))}
        </div>
      )}
      <ul className="space-y-1">
        {videos.map((v) => (
          <li key={v.videoId} className="flex items-baseline gap-1.5 text-[12px]">
            <span className="shrink-0 font-mono text-[10px] tabular-nums text-terminal-ink-muted">
              {shortDate(v.observedAt)}
            </span>
            <a
              href={v.url}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex min-w-0 items-baseline gap-1 text-terminal-ink hover:underline"
            >
              <span className="truncate">{v.title || v.url}</span>
              <ExternalLink className="h-3 w-3 shrink-0 self-center opacity-50 group-hover:opacity-100" />
            </a>
            <span className="shrink-0 text-[10px] text-terminal-ink-muted">
              {v.isGary ? (
                <span className="text-brand-magenta">{v.channelTitle} · Gary</span>
              ) : (
                v.channelTitle
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function YouTubeVideoListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-1">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-5 animate-pulse rounded bg-white/5" />
      ))}
    </div>
  );
}
